import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { ScrollView, Switch } from 'tamagui'
import { useCreatePlaceMutation, useShareToFeedMutation, DuplicatePlaceError } from '@cobu/api-client'
import type { Place } from '@cobu/types'
import { CobuBackButton, CobuButton, CobuContainer, CobuHeader, CobuInput, CobuStack, CobuText } from '@cobu/ui'
import { RootState } from '../../store'
import { createPlaceSchema, placeDefaultValues, type PlaceFormValues } from '../../schemas/placeSchema'
import { CATEGORY_META, CATEGORY_ORDER, getCategoryLabel } from '../../components/places/categoryMeta'
import { showErrorMessage, showSuccessMessage } from '../../utils/messageToast'
import { Strings } from '../../constants/Strings'

const DESCRIPTION_MAX = 500

export default function AddPlaceScreen() {
  const navigate = useNavigate()
  const user = useSelector((state: RootState) => state.auth.user)
  const buildingId = user?.building?.uuid || ''

  const createPlaceMutation = useCreatePlaceMutation(buildingId)
  const shareToFeedMutation = useShareToFeedMutation()
  const placeSchema = useMemo(() => createPlaceSchema(), [])
  const strings = Strings.get().places

  // "Also share to Feed?" — defaulted ON per the original spec.
  const [alsoShareToFeed, setAlsoShareToFeed] = useState(true)

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<PlaceFormValues>({
    resolver: zodResolver(placeSchema),
    defaultValues: placeDefaultValues,
    mode: 'onChange',
  })

  // Set when the server rejects a create as a duplicate; drives the warning banner.
  const [duplicate, setDuplicate] = useState<Place | null>(null)

  const submit = async (data: PlaceFormValues, confirmDuplicate = false) => {
    if (!user) return

    try {
      const place = await createPlaceMutation.mutateAsync({
        input: { ...data, confirmDuplicate },
      })

      // Optional second call: reuse the Feed pipeline to cross-post the new
      // place. No placePostType — auto-shares carry no type badge. A share
      // failure is non-fatal; the place was still created.
      if (alsoShareToFeed) {
        try {
          await shareToFeedMutation.mutateAsync({
            place: place.uuid,
            comment: data.description?.trim() || Strings.format(strings.add.defaultShareComment, { name: place.name }),
          })
        } catch {
          // swallow — don't block a successful place creation on the feed share
        }
      }

      showSuccessMessage(strings.add.addedToast)
      // Land on the new place's detail (PRD §3.3), not the list.
      navigate(`/places/${place.uuid}`, { replace: true })
    } catch (error) {
      if (error instanceof DuplicatePlaceError) {
        setDuplicate(error.existingPlace)
        return
      }
      showErrorMessage(strings.genericError)
    }
  }

  const onSubmit = (data: PlaceFormValues) => submit(data, false)

  return (
    <CobuContainer f={1} bg="$background">
      <CobuHeader title={strings.add.title} renderLeft={() => <CobuBackButton onPress={() => navigate(-1)} />} />

      <ScrollView>
        <CobuStack p="$4" gap="$4">
          <Controller
            control={control}
            name="name"
            render={({ field: { onChange, onBlur, value } }) => (
              <CobuInput
                label={strings.add.nameLabel}
                placeholder={strings.add.namePlaceholder}
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                error={errors.name?.message}
              />
            )}
          />

          <Controller
            control={control}
            name="street"
            render={({ field: { onChange, onBlur, value } }) => (
              <CobuInput
                label={strings.add.streetLabel}
                placeholder={strings.add.streetPlaceholder}
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                error={errors.street?.message}
              />
            )}
          />

          <CobuStack layout="horizontal" gap="$3">
            <CobuStack f={1}>
              <Controller
                control={control}
                name="city"
                render={({ field: { onChange, onBlur, value } }) => (
                  <CobuInput
                    label={strings.add.cityLabel}
                    placeholder={strings.add.cityPlaceholder}
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    error={errors.city?.message}
                  />
                )}
              />
            </CobuStack>
            <CobuStack f={1}>
              <Controller
                control={control}
                name="zip"
                render={({ field: { onChange, onBlur, value } }) => (
                  <CobuInput
                    label={strings.add.zipLabel}
                    placeholder={strings.add.zipPlaceholder}
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    error={errors.zip?.message}
                  />
                )}
              />
            </CobuStack>
          </CobuStack>

          <CobuStack>
            <CobuText variant="label" color="$color" mb="$2">{strings.add.categoryLabel}</CobuText>
            <Controller
              control={control}
              name="category"
              render={({ field: { onChange, value } }) => (
                <CobuStack style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {CATEGORY_ORDER.map((category) => {
                    const meta = CATEGORY_META[category]
                    const Icon = meta.icon
                    const selected = value === category
                    return (
                      <CobuStack
                        key={category}
                        layout="horizontal"
                        ai="center"
                        jc="center"
                        gap="$2"
                        py="$3"
                        br="$3"
                        bg={selected ? '$color' : 'transparent'}
                        borderWidth={1}
                        borderColor={selected ? '$color' : '$borderColor'}
                        onPress={() => onChange(category)}
                      >
                        <Icon style={{ fontSize: 16, color: selected ? 'var(--background)' : undefined }} />
                        <CobuText variant="body" fontSize="$2" fontWeight="500" color={selected ? '$background' : '$color'}>
                          {getCategoryLabel(category)}
                        </CobuText>
                      </CobuStack>
                    )
                  })}
                </CobuStack>
              )}
            />
          </CobuStack>

          <Controller
            control={control}
            name="description"
            render={({ field: { onChange, onBlur, value } }) => (
              <CobuStack gap="$1">
                <CobuInput
                  label={strings.add.descriptionLabel}
                  placeholder={strings.add.descriptionPlaceholder}
                  value={value}
                  onChangeText={(text: string) => onChange(text.slice(0, DESCRIPTION_MAX))}
                  onBlur={onBlur}
                  multiline
                  numberOfLines={3}
                  error={errors.description?.message}
                />
                <CobuText variant="caption" color="$colorSecondary" style={{ alignSelf: 'flex-end' }}>
                  {(value?.length ?? 0)}/{DESCRIPTION_MAX}
                </CobuText>
              </CobuStack>
            )}
          />

          <CobuStack
            layout="horizontal"
            ai="center"
            jc="space-between"
            p="$3"
            br="$3"
            bg="$backgroundSecondary"
            borderWidth={1}
            borderColor="$borderColor"
            onPress={() => setAlsoShareToFeed((v) => !v)}
          >
            <CobuStack f={1} pr="$3">
              <CobuText variant="body" fontWeight="600" fontSize="$3">{strings.add.shareToFeedTitle}</CobuText>
              <CobuText variant="caption" color="$colorSecondary">
                {strings.add.shareToFeedSubtitle}
              </CobuText>
            </CobuStack>
            <Switch
              size="$3"
              checked={alsoShareToFeed}
              onCheckedChange={setAlsoShareToFeed}
              aria-label={strings.add.shareToFeedTitle}
              width={47}
              height={27}
              borderRadius={9999}
              borderWidth={1}
              borderColor={alsoShareToFeed ? ('$cobuTopaz' as any) : ('$borderColorLight' as any)}
              backgroundColor={alsoShareToFeed ? ('$cobuTopaz' as any) : ('$colorMuted' as any)}
              animation="bouncy"
            >
              <Switch.Thumb backgroundColor="$white" width={25} height={25} borderRadius={9999} animation="bouncy" />
            </Switch>
          </CobuStack>

          {duplicate && (
            <CobuStack gap="$2" p="$3" br="$3" bg="$backgroundSecondary" borderWidth={1} borderColor="$borderColor">
              <CobuText variant="body" fontWeight="600" fontSize="$3">
                {strings.add.duplicateTitle}
              </CobuText>
              <CobuText variant="caption" color="$colorSecondary">
                {duplicate.name} — {duplicate.street}
              </CobuText>
              <CobuStack layout="horizontal" gap="$2" mt="$1">
                <CobuStack f={1}>
                  <CobuButton
                    dsVariant="outline"
                    title={strings.add.duplicateView}
                    onPress={() => navigate(`/places/${duplicate.uuid}`)}
                  />
                </CobuStack>
                <CobuStack f={1}>
                  <CobuButton
                    dsVariant="primary"
                    title={strings.add.duplicateAddAnyway}
                    loading={createPlaceMutation.isPending}
                    onPress={handleSubmit((data) => submit(data, true))}
                  />
                </CobuStack>
              </CobuStack>
            </CobuStack>
          )}

          <CobuButton
            dsVariant="primary"
            title={strings.add.submit}
            loading={createPlaceMutation.isPending}
            onPress={handleSubmit(onSubmit)}
          />
        </CobuStack>
      </ScrollView>
    </CobuContainer>
  )
}
